import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { numberToWords } from "../Inovice/utils";
import { formatNumberWithCommas } from "../../utils";
import { useState } from "react";

 const PDFGenerator = ({ data, isChecked, fileName = "tax_invoice.pdf" }) => {

  const [showPopup, setShowPopup] = useState(false);
    const [description, setDescription] = useState("");
  
    const invoiceData = data && data?.length > 0 ? data[0] : {};
    const placeholder = "N/A"; // Placeholder for unavailable data
  
    const uniqueInvoices = Array.from(
      new Map(data?.map((item) => [item.VBELN, item])).values()
    );
  
    const parseNumber = (value) => parseFloat(value?.toString().replace(/,/g, "")) || 0;
  
    // Calculate totals from all data (only for ZPDISC and ZVAT)
    const totals = data.reduce(
      (acc, item) => {
        acc.ZPDISC += parseNumber(item.ZPDISC || 0);
        acc.ZVAT += parseNumber(item.ZVAT || 0);
        acc.ZPVAT = parseNumber(item.ZPVAT || 0); // Assuming ZPVAT is a percentage value in data
        return acc;
      },
      {
        ZPDISC: 0,
        ZVAT: 0,
        ZPVAT: 0,
      }
    );
  
    // Calculate totals from uniqueInvoices
    totals.ZAVAT = uniqueInvoices.reduce((sum, item) => sum + parseNumber(item.ZAVAT || 0), 0);
    totals.ZADV = uniqueInvoices.reduce((sum, item) => sum + parseNumber(item.ZADV || 0), 0);
    totals.ZRET = uniqueInvoices.reduce((sum, item) => sum + parseNumber(item.ZRET || 0), 0);
  
    // Updated Calculations
    const ZPVATAmount = (totals.ZPVAT / 100) * totals.ZRET; // Calculate ZPVAT percentage of ZRET
    totals.ZVATafterZRET = totals.ZVAT - ZPVATAmount; // Adjusted calculation
  
    totals.ZBAMT = totals.ZPDISC - totals.ZADV;
    totals.ZBTAMT = totals.ZVATafterZRET - totals.ZAVAT;
    totals.ZFAMT = totals.ZBAMT - totals.ZRET + totals.ZBTAMT;


function generatePDF(data, fileName = "tax_invoice.pdf") {
  const doc = new jsPDF({
    unit: "mm",
    format: "a4",
    orientation: "portrait",
  });

  // Constants for layout
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;
  const footerReserve = 55;

  // Fixed header dimensions
  const logoHeight = 25;
  const logoTopMargin = margin;
  const billToBoxTop = logoTopMargin + logoHeight + 5;
  const trnBoxHeight = 10;
  let billToBoxHeight = 50;
  let trnBoxTop = billToBoxTop + billToBoxHeight;
  let headerEndY = trnBoxTop + trnBoxHeight + 3;

  // Helper function to wrap text
  const wrapText = (text, width) =>
    doc.splitTextToSize(text || placeholder, width);

  // ==================== HEADER SECTION (on every page) ====================
  const drawHeader = () => {
    const colWidth = contentWidth / 3;
    const rowHeight = 4;

    // Logo
    doc.addImage(
      "/DUNES_LOGO.png",
      "PNG",
      margin,
      logoTopMargin,
      80,
      logoHeight
    );

    // TAX INVOICE title - right aligned
    doc.setFontSize(22);
    doc.setFont("helvetica", "bold");
    doc.text("TAX INVOICE", pageWidth - margin, logoTopMargin + 15, {
      align: "right",
    });

    const billToLines = [
      invoiceData.ZBP || placeholder,
      invoiceData.NAME1 || placeholder,
      invoiceData.CITY1 || placeholder,
      `PO BOX: ${invoiceData.POST_CODE1 || placeholder}`,
      invoiceData.STREET || placeholder,
      // invoiceData.LANDX50 || placeholder,
    ];
    const shipToLines = [
      invoiceData.ZSP || placeholder,
      invoiceData.ZNAME1 || placeholder,
      invoiceData.ZCITY1 || placeholder,
      `PO BOX: ${invoiceData.ZPOST_CODE1 || placeholder}`,
      invoiceData.ZSTREET || placeholder,
      // invoiceData.ZLANDX50 || placeholder,
    ];
    const invoiceDetails = [
      { label: "INVOICE NO", value: invoiceData.VBELN || placeholder },
      { label: "DATE", value: invoiceData.FKDAT || placeholder },
      { label: "SALES REP", value: invoiceData.ENAME || placeholder },
      { label: "PAYMENT", value: invoiceData.TEXT1 || placeholder },
      { label: "PO REF NO", value: invoiceData.BSTNK_VF || placeholder },
      { label: "DELIVERY NO", value: invoiceData.VGBEL || placeholder },
    ];

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);

    const countWrappedLines = (lines, width) =>
      lines.reduce((sum, line) => sum + wrapText(line, width).length, 0);

    const boxPaddingTop = 4;
    const boxPaddingBottom = 1;
    const billToLineCount = 1 + countWrappedLines(billToLines, colWidth - 6);
    const shipToLineCount = 1 + countWrappedLines(shipToLines, colWidth - 6);
    const invoiceLineCount = invoiceDetails.length;
    const maxLineCount = Math.max(billToLineCount, shipToLineCount, invoiceLineCount);

    billToBoxHeight = boxPaddingTop + boxPaddingBottom + maxLineCount * rowHeight;
    trnBoxTop = billToBoxTop + billToBoxHeight;
    headerEndY = trnBoxTop + trnBoxHeight + 3;

    // Bill To / Ship To / Invoice Details Box
    doc.setDrawColor(0);
    doc.setLineWidth(0.3);
    doc.rect(margin, billToBoxTop, contentWidth, billToBoxHeight);

    // Draw vertical dividers
    doc.line(margin + colWidth, billToBoxTop, margin + colWidth, billToBoxTop + billToBoxHeight);
    doc.line(margin + colWidth * 2, billToBoxTop, margin + colWidth * 2, billToBoxTop + billToBoxHeight);

    // BILL TO Section
    let y1 = billToBoxTop + boxPaddingTop;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text("BILL TO:", margin + 3, y1);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    y1 += rowHeight;

    billToLines.forEach((line) => {
      const lines = wrapText(line, colWidth - 6);
      lines.forEach((wrappedLine) => {
        doc.text(wrappedLine, margin + 3, y1);
        y1 += rowHeight;
      });
    });

    // SHIP TO Section
    let y2 = billToBoxTop + boxPaddingTop;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text("SHIP TO:", margin + colWidth + 3, y2);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    y2 += rowHeight;

    shipToLines.forEach((line) => {
      const lines = wrapText(line, colWidth - 6);
      lines.forEach((wrappedLine) => {
        doc.text(wrappedLine, margin + colWidth + 3, y2);
        y2 += rowHeight;
      });
    });

    // Invoice Details Section (right column) - Two column layout
    let y3 = billToBoxTop + boxPaddingTop;
    const labelX = margin + colWidth * 2 + 3;
    const valueX = margin + colWidth * 2 + 30;
    doc.setFontSize(7);

    invoiceDetails.forEach((detail) => {
      doc.setFont("helvetica", "bold");
      doc.text(detail.label, labelX, y3);
      doc.setFont("helvetica", "normal");
      // Wrap value if too long
      const wrappedValue = wrapText(detail.value, colWidth - 35);
      doc.text(wrappedValue[0] || placeholder, valueX, y3);
      y3 += rowHeight;
    });

    // TRN Section
    doc.rect(margin, trnBoxTop, contentWidth, trnBoxHeight);
    doc.line(margin + colWidth, trnBoxTop, margin + colWidth, trnBoxTop + trnBoxHeight);
    doc.line(margin + colWidth * 2, trnBoxTop, margin + colWidth * 2, trnBoxTop + trnBoxHeight);

    const trnY = trnBoxTop + 6;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.text(`TRN: ${invoiceData.ZBPTRN || placeholder}`, margin + 3, trnY);
    doc.text(`TRN: ${invoiceData.ZSPTRN || placeholder}`, margin + colWidth + 3, trnY);
    doc.text(`TRIGON TRN: ${invoiceData.ZCTRN || placeholder}`, margin + colWidth * 2 + 3, trnY);

    return headerEndY;
  };

  // ==================== FOOTER SECTION (on every page) ====================
  const drawFooter = (pageNum, totalPages) => {
    const footerLineY = pageHeight - 38;
    const authorizedY = footerLineY - 2;
    const footerTextStartY = footerLineY + 4;
    const footerLineHeight = 3.5;
    const certifiedY = authorizedY - 8;
    const forCompanyY = authorizedY - 4;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.text(
      "Certified that the particulars given above are true and correct",
      margin,
      certifiedY
    );
    doc.text("FOR DUNES TECHNOLOGIES LLC", margin, forCompanyY);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.text("AUTHORIZED SIGNATORY", margin, authorizedY);

    doc.setDrawColor(0);
    doc.setLineWidth(0.3);
    doc.line(margin, footerLineY, pageWidth - margin, footerLineY);

    // Bank Details - centered
    doc.setFontSize(6);
    doc.text(
      "Bank Details: DUNES TECHNOLOGIES LLC, Emirates NBD, Dubai, UAE.",
      pageWidth / 2,
      footerTextStartY,
      { align: "center" }
    );

    doc.setFont("helvetica", "normal");
    const bankDetails = [
      "Dirham Account No.: 101 5855556 401, Dirham IBAN No.: AE820260001015855556401",
      "Dollar Account No.: 102 5855556 402, Dollar IBAN No.: AE900260001025855556402",
      "Swift Code: EBILAEAD",
      "Routing Code: 302620122",
    ];

    bankDetails.forEach((line, index) => {
      doc.text(
        line,
        pageWidth / 2,
        footerTextStartY + (index + 1) * footerLineHeight,
        { align: "center" }
      );
    });

    doc.setFont("helvetica", "bold");
    doc.text(
      "Payment to be made by crossed cheques in favour of DUNES TECHNOLOGIES LLC",
      pageWidth / 2,
      footerTextStartY + (bankDetails.length + 1) * footerLineHeight,
      { align: "center" }
    );

    doc.setFont("helvetica", "bold");
    doc.text(
      "Website: www.dunestech.ae",
      pageWidth / 2,
      footerTextStartY + (bankDetails.length + 2) * footerLineHeight,
      { align: "center" }
    );

    // Page number
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(`Page No:${pageNum}`, margin, pageHeight - 8);
  };

  // ==================== ITEMS TABLE ====================
  const drawItemsTable = (startY) => {
    const headers = [
      "S.NO",
      "ITEM",
      "DESCRIPTION",
      "QTY",
      "RATE",
      "DISC",
      "Price After\nDiscount",
      "VAT\nRate%",
      "VAT\nAmount",
      "Total\nAmount",
    ];

    const rows = data?.map((item, index) => [
      index + 1,
      item.MATNR || placeholder,
      item.ARKTX || placeholder,
      item.FKIMG || placeholder,
      formatNumberWithCommas(item.ZRATE) || placeholder,
      item.ZDISC || placeholder,
      formatNumberWithCommas(item.ZPDISC) || placeholder,
      item.ZPVAT || placeholder,
      formatNumberWithCommas(item.ZVAT) || placeholder,
      formatNumberWithCommas(item.ZTAMT) || placeholder,
    ]);

    doc.autoTable({
      head: [headers],
      body: rows,
      startY: startY,
      margin: {
        top: headerEndY,
        left: margin,
        right: margin,
        bottom: footerReserve,
      },
      styles: {
        fontSize: 7,
        cellPadding: 2,
        lineColor: [0, 0, 0],
        lineWidth: 0.2,
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        halign: "center",
        valign: "middle",
        lineColor: [0, 0, 0],
        lineWidth: 0.2,
      },
      bodyStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        valign: "middle",
      },
      columnStyles: {
        0: { cellWidth: 10, halign: "center" }, // S.NO
        1: { cellWidth: 24 }, // ITEM
        2: { cellWidth: 36 }, // DESCRIPTION
        3: { cellWidth: 12, halign: "center" }, // QTY
        4: { cellWidth: 16, halign: "right" }, // RATE
        5: { cellWidth: 12, halign: "center" }, // DISC
        6: { cellWidth: 20, halign: "right" }, // Price After Disc
        7: { cellWidth: 12, halign: "center" }, // VAT %
        8: { cellWidth: 16, halign: "right" }, // VAT Amt
        9: { cellWidth: 22, halign: "right" }, // Total Amt
      },
      didDrawPage: function (data) {
        drawHeader();
      },
      showHead: "everyPage",
    });

    return doc.lastAutoTable.finalY;
  };

  // ==================== TOTALS SECTION ====================
  const drawTotalsSection = (startY) => {
    let yPos = startY + 5;
    const colWidth = contentWidth / 2;
    const rowHeight = 5.5;

    const totalsRows = [
      ["Total Amount Before Tax:", formatNumberWithCommas(totals.ZPDISC.toFixed(2))],
      ["Total Retention Amount:", formatNumberWithCommas(totals.ZRET.toFixed(2))],
      ["Total VAT Amount after Retention:", formatNumberWithCommas(totals.ZVATafterZRET.toFixed(2))],
      ["Advance Received:", formatNumberWithCommas(totals.ZADV.toFixed(2))],
      ["Balance Amount Before Tax:", formatNumberWithCommas(totals.ZBAMT.toFixed(2))],
      ["Tax on Advance:", formatNumberWithCommas(totals.ZAVAT.toFixed(2))],
      ["Balance Tax Amount:", formatNumberWithCommas(totals.ZBTAMT.toFixed(2))],
      ["Balance Amount After Tax:", formatNumberWithCommas(totals.ZFAMT.toFixed(2))],
    ];

    const sectionHeight = totalsRows.length * rowHeight + 4;

    // Check if we need a new page for totals
    const descriptionHeight = 8;
    const totalsHeight = sectionHeight + 5 + descriptionHeight;
    if (yPos + totalsHeight > pageHeight - footerReserve) {
      doc.addPage();
      drawHeader();
      yPos = headerEndY + 5;
    }

    // Draw boxes
    doc.setLineWidth(0.2);
    doc.rect(margin, yPos, colWidth, sectionHeight);
    doc.rect(margin + colWidth, yPos, colWidth, sectionHeight);

    // Amount in Words (left box)
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.text("AMOUNT IN WORDS:", margin + 3, yPos + 5);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6);

    const words = "AED " + numberToWords(totals.ZFAMT);
    const splitWords = doc.splitTextToSize(words, colWidth - 8);
    let wordY = yPos + 10;
    splitWords.forEach((line) => {
      doc.text(line, margin + 3, wordY);
      wordY += 4;
    });

    // Totals table (right box)
    const leftLabelX = margin + colWidth + 3;
    const rightValueX = margin + contentWidth - 3;
    let totalsY = yPos + 5;

    doc.setFontSize(6);

    // Draw horizontal lines and content for each row
    totalsRows.forEach((row, i) => {
      if (i > 0) {
        doc.line(
          margin + colWidth,
          yPos + (i * rowHeight) + 1.5,
          margin + contentWidth,
          yPos + (i * rowHeight) + 1.5
        );
      }

      doc.setFont("helvetica", "bold");
      doc.text(row[0], leftLabelX, totalsY);
      doc.setFont("helvetica", "normal");
      doc.text(row[1], rightValueX, totalsY, { align: "right" });
      totalsY += rowHeight;
    });

    // Draw vertical line in totals section
    const dividerX = margin + colWidth + colWidth * 0.65;
    doc.line(dividerX, yPos, dividerX, yPos + sectionHeight);

    yPos += sectionHeight + 5;

    // Description
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.text(`Description: ${description || placeholder}`, margin, yPos);

    return yPos;
  };

  // ==================== GENERATE PDF ====================
  const tableStartY = drawHeader();
  const tableEndY = drawItemsTable(tableStartY);
  drawTotalsSection(tableEndY);

  // Add footer to all pages
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(i, totalPages);
  }

  // Save the PDF
  doc.save(fileName);
}

return (
  <>
  <button
    className={`px-6 py-2 rounded-md shadow ml-4 text-white ${isChecked ? "bg-blue-500 hover:bg-blue-600" : "bg-gray-400 cursor-not-allowed"
      }`}
    disabled={!isChecked}
    onClick={() => setShowPopup(true)}
    >
    Dunes PDF
  </button>
  {showPopup && (
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white p-6 rounded-lg shadow-lg w-96">
          <h2 className="text-lg font-semibold mb-4">Enter Description</h2>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter description..."
            className="w-full h-24 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          <div className="flex justify-end mt-4">
            <button
              onClick={() => setShowPopup(false)}
              className="mr-2 px-4 py-2 bg-gray-400 text-white rounded-md hover:bg-gray-500"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                setShowPopup(false);
                generatePDF(data);
              }}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
            >
              Submit & Download
            </button>
          </div>
        </div>
      </div>
    )}
  </>
);
}

export default PDFGenerator;
